import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { APP_NAME } from '@shared/brand';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import { useToast } from '../lib/toast';
import { useTheme } from '../lib/theme';
import { BrandMark } from './Brand';
import { Icon } from './Icon';
import type { IconName } from './Icon';
import { TopbarContext } from './PageHeader';

type Access = ReturnType<typeof useAuth>;

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  show: (access: Access) => boolean;
  end?: boolean;
}

interface NavGroup {
  heading?: string;
  items: NavItem[];
}

const inCommunity = (a: Access) => Boolean(a.user?.communityId);

const NAV: NavGroup[] = [
  {
    items: [
      { to: '/admin', label: 'Communities', icon: 'building', show: (a) => a.isSuperAdmin, end: true },
      { to: '/admin/visitors', label: 'Visitor analytics', icon: 'globe', show: (a) => a.isSuperAdmin },
      { to: '/admin/usage', label: 'App analytics', icon: 'chart', show: (a) => a.isSuperAdmin },
      { to: '/admin/settings', label: 'Settings', icon: 'key', show: (a) => a.isSuperAdmin },
      { to: '/', label: 'Neighborhood', icon: 'home', show: inCommunity, end: true },
    ],
  },
  {
    heading: 'Community',
    items: [
      { to: '/events', label: 'Events', icon: 'calendar', show: inCommunity },
      { to: '/requests', label: 'Complaints & Help', icon: 'life-buoy', show: inCommunity },
      { to: '/violations', label: 'Violations', icon: 'alert', show: inCommunity },
      { to: '/architecture', label: 'Architecture', icon: 'ruler', show: inCommunity },
      { to: '/bylaws', label: 'Bylaws', icon: 'book', show: inCommunity },
    ],
  },
  {
    heading: 'Board',
    items: [
      { to: '/board', label: 'Board Hub', icon: 'dash', show: (a) => a.isBoard, end: true },
      { to: '/financials', label: 'Financials', icon: 'wallet', show: (a) => a.canUseFinance },
      { to: '/newsletter', label: 'Newsletter', icon: 'paper', show: (a) => a.isBoard },
      { to: '/board/residents', label: 'Members', icon: 'users', show: (a) => a.isBoard },
      {
        to: '/community',
        label: 'Community',
        icon: 'building',
        // Super Admins edit community details from Community Management unless managing one.
        show: (a) => a.isBoard && (!a.isSuperAdmin || Boolean(a.user?.actingCommunityId)),
      },
    ],
  },
];

const COLLAPSE_KEY = 'hoa-sidebar';

/** Lets a Super Admin pick the community they are managing, or return to the platform view. */
function CommunitySwitcher() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { fail } = useToast();
  const [busy, setBusy] = useState(false);
  const options = useAsync(async () => (await api.adminCommunityOptions()).communities, []);
  const selected = auth.user?.communityId ?? '';

  async function choose(id: string) {
    setBusy(true);
    try {
      const { user, community } = await api.adminActInCommunity(id || null);
      auth.setUser(user);
      auth.setCommunity(community);
      navigate(id ? '/board' : '/admin');
    } catch (error) {
      fail(error, 'The community was not switched.');
    } finally {
      setBusy(false);
    }
  }

  const list = options.data ?? [];
  const known = list.some((item) => item.id === selected);
  return (
    <label className="sb-switch" title="Community you are managing">
      <small>Managing</small>
      <select
        value={selected}
        disabled={busy}
        onFocus={() => void options.reload()}
        onChange={(event) => void choose(event.target.value)}
        aria-label="Community you are managing"
      >
        <option value="">All communities (platform)</option>
        {selected && !known && auth.community && (
          <option value={selected}>{auth.community.name}</option>
        )}
        {list.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
            {item.status === 'disabled' ? ' (disabled)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

function initials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function roleLabel(access: Access): string {
  const roles = access.user?.roles ?? [];
  if (roles.includes('superadmin')) return 'Super admin';
  if (roles.includes('admin')) return 'Community admin';
  if (roles.includes('board')) return 'Board member';
  if (roles.includes('arb')) return 'Architecture review';
  if (roles.includes('organizer')) return 'Event organizer';
  return access.user?.isTenant ? 'Tenant' : 'Member';
}

export function Layout() {
  const auth = useAuth();
  const theme = useTheme();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === 'collapsed';
    } catch {
      return false;
    }
  });
  const [titleSlot, setTitleSlot] = useState<HTMLDivElement | null>(null);
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);
  const footRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    setMenuOpen(false);
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle('sb-collapsed', collapsed);
    try {
      if (collapsed) localStorage.setItem(COLLAPSE_KEY, 'collapsed');
      else localStorage.removeItem(COLLAPSE_KEY);
    } catch {
      // Storage can be unavailable in private windows; the sidebar still works.
    }
  }, [collapsed]);

  useEffect(() => {
    document.body.classList.toggle('sb-open', drawerOpen);
  }, [drawerOpen]);

  useEffect(
    () => () => {
      document.body.classList.remove('sb-collapsed', 'sb-open');
    },
    [],
  );

  useEffect(() => {
    if (!menuOpen && !drawerOpen) return;
    const onPointer = (event: MouseEvent) => {
      if (!footRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      setDrawerOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, drawerOpen]);

  const groups = NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.show(auth)),
  })).filter((group) => group.items.length > 0);

  const current = groups
    .flatMap((group) => group.items)
    .filter((item) =>
      item.end ? location.pathname === item.to : location.pathname.startsWith(item.to),
    )
    .sort((a, b) => b.to.length - a.to.length)[0];

  const showCommunityInfo = inCommunity(auth) && !auth.isBoard && !auth.isSuperAdmin;
  const brandName = auth.community?.name ?? APP_NAME;
  const slots = useMemo(() => ({ title: titleSlot, actions: actionsSlot }), [titleSlot, actionsSlot]);
  const closeMenu = () => setMenuOpen(false);

  // A Super Admin outside any community only has the platform pages and their own account.
  const platformOnly = auth.isSuperAdmin && !auth.user?.communityId;
  if (
    platformOnly &&
    !location.pathname.startsWith('/admin') &&
    !location.pathname.startsWith('/profile') &&
    location.pathname !== '/'
  ) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <TopbarContext.Provider value={slots}>
      <div className="shell">
        <aside className="sidebar" aria-label="Main">
          <NavLink to="/" className="sb-brand" title={brandName}>
            <BrandMark />
            <span>
              <b>{brandName}</b>
              <small>{auth.community ? APP_NAME : 'Platform'}</small>
            </span>
          </NavLink>

          {auth.isSuperAdmin && <CommunitySwitcher />}

          <nav className="sb-nav" aria-label="Sections">
            {groups.map((group, index) => (
              <div className="sb-group" key={group.heading ?? index}>
                {group.heading && <h6>{group.heading}</h6>}
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    title={item.label}
                    className={({ isActive }) => `sb-link${isActive ? ' active' : ''}`}
                  >
                    <Icon name={item.icon} />
                    <span className="lbl">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          <div className="sb-foot" ref={footRef}>
            <div className={`menu-pop${menuOpen ? ' open' : ''}`} role="menu">
              {showCommunityInfo && (
                <NavLink to="/community-info" role="menuitem" onClick={closeMenu}>
                  <Icon name="info" />
                  Community information
                </NavLink>
              )}
              <NavLink to="/profile" role="menuitem" onClick={closeMenu}>
                <Icon name="user" />
                Account settings
              </NavLink>
              <NavLink to="/profile#password" role="menuitem" onClick={closeMenu}>
                <Icon name="key" />
                Change password
              </NavLink>
              <hr />
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  closeMenu();
                  void auth.logout();
                }}
              >
                <Icon name="logout" />
                Sign out
              </button>
            </div>
            <button
              type="button"
              className="sb-user"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="avatar">{initials(auth.user?.name)}</span>
              <span className="sb-who">
                <b>{auth.user?.name ?? 'Account'}</b>
                <small>{roleLabel(auth)}</small>
              </span>
              <Icon name="chevron" />
            </button>
          </div>
        </aside>
        <div className="scrim" onClick={() => setDrawerOpen(false)} />

        <div className="workspace">
          <header className="topbar">
            <button
              type="button"
              className="btn ghost icon tb-menu"
              aria-label="Open menu"
              onClick={() => setDrawerOpen(true)}
            >
              <Icon name="menu" />
            </button>
            <button
              type="button"
              className="btn ghost icon tb-collapse"
              aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              onClick={() => setCollapsed((value) => !value)}
            >
              <Icon name="panel" />
            </button>
            <div className="tb-title">
              <div className="tb-slot" ref={setTitleSlot} />
              <h1 className="tb-default">{current?.label ?? brandName}</h1>
            </div>
            <div className="tb-actions" ref={setActionsSlot} />
            <div className="tb-tools">
              <button
                type="button"
                className="btn ghost icon"
                onClick={theme.cycle}
                title={`Theme: ${theme.preference}`}
                aria-label={`Switch theme, currently ${theme.preference}`}
              >
                <Icon
                  name={
                    theme.preference === 'dark' ? 'moon' : theme.preference === 'light' ? 'sun' : 'theme'
                  }
                />
              </button>
            </div>
          </header>

          <main className="app-main">
            {/* Remount the page when a Super Admin switches communities so it reloads its data. */}
            <Outlet key={auth.user?.communityId ?? 'platform'} />
          </main>
        </div>
      </div>
    </TopbarContext.Provider>
  );
}
